import React from "react";

import { calendarStats, formatPnl, formatPnlCell, formatPnlFull, monthCells, yearCells, yearStats } from "./tagCalendarModel";
import styles from "./tagPnlCalendar.module.css";

const WEEKDAYS = ["一", "二", "三", "四", "五"];  // A 股周末不开盘

function tone(value) {
  if (value === null || value === undefined || value === 0) return "";
  return value > 0 ? styles.up : styles.down;
}

function Stat({ label, value, sub, cls = "" }) {
  return (
    <div className={styles.stat}>
      <span>{label}</span>
      <b className={cls}>{value}</b>
      {sub ? <small>{sub}</small> : null}
    </div>
  );
}

// 「最好 / 最差」的副标题：月视图写日期（MM-DD），年视图写第几月。
function when(point, mode) {
  if (!point) return "";
  return mode === "month" ? point.date.slice(5) : `${Number(point.date.slice(5))} 月`;
}

// 收益日历（原型 v23 的右侧卡片）：月 / 年切换、‹ › 翻页、合计，下面 2×2 统计。series 是标签汇总接口的逐日点。
export function TagPnlCalendar({ series }) {
  const lastDate = series.at(-1)?.date ?? "";
  const firstDate = series[0]?.date ?? "";
  const [mode, setMode] = React.useState("month");
  const [cursor, setCursor] = React.useState(lastDate.slice(0, 7));
  const year = cursor.slice(0, 4);
  const range = mode === "month" ? cursor : year;
  const stats = mode === "month" ? calendarStats(series, range) : yearStats(series, year);

  const step = (delta) => {
    const [y, m] = cursor.split("-").map(Number);
    const next = mode === "month" ? new Date(y, m - 1 + delta, 1) : new Date(y + delta, m - 1, 1);
    setCursor(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`);
  };
  const atStart = range <= firstDate.slice(0, range.length);
  const atEnd = range >= lastDate.slice(0, range.length);
  const unit = mode === "month" ? "天" : "个月";
  const noun = mode === "month" ? "一天" : "月";

  return (
    <section className={styles.card} aria-label="收益日历">
      <div className={styles.head}>
        <h2>收益日历</h2>
        <div className={styles.modes} role="group" aria-label="日历粒度">
          <button type="button" aria-pressed={mode === "month"} onClick={() => setMode("month")}>月</button>
          <button type="button" aria-pressed={mode === "year"} onClick={() => setMode("year")}>年</button>
        </div>
      </div>
      <div className={styles.nav}>
        <button type="button" onClick={() => step(-1)} disabled={atStart} aria-label="上一期">‹</button>
        <b>{mode === "month" ? `${year} 年 ${Number(cursor.slice(5))} 月` : `${year} 年`}</b>
        <button type="button" onClick={() => step(1)} disabled={atEnd} aria-label="下一期">›</button>
        <span className={`${styles.total} ${tone(stats.total)}`}>合计 {formatPnl(stats.total)}</span>
      </div>
      {mode === "month" ? (
        <div className={styles.grid}>
          {WEEKDAYS.map((label) => <span key={label} className={styles.weekday}>{label}</span>)}
          {monthCells(series, cursor).map((cell, index) => {
            if (cell === null) return <span key={`blank-${index}`} />;
            if (cell.pnl === null) return <span key={cell.date} className={`${styles.cell} ${styles.off}`}>{cell.day}</span>;
            return (
              <span key={cell.date} className={`${styles.cell} ${tone(cell.pnl)}`} title={`${cell.date} ${formatPnlFull(cell.pnl)}`}>
                <b>{cell.day}</b>
                {formatPnlCell(cell.pnl)}
              </span>
            );
          })}
        </div>
      ) : (
        <div className={`${styles.grid} ${styles.year}`}>
          {yearCells(series, year).map((cell) => {
            const label = `${Number(cell.month.slice(5))} 月`;
            if (cell.pnl === null) return <span key={cell.month} className={`${styles.cell} ${styles.off}`}><b>{label}</b></span>;
            return (
              <button
                type="button"
                key={cell.month}
                className={`${styles.cell} ${styles.monthButton} ${tone(cell.pnl)}`}
                title={`点开看 ${label}每天`}
                onClick={() => { setCursor(cell.month); setMode("month"); }}
              >
                <b>{label}</b>
                {formatPnl(cell.pnl)}
              </button>
            );
          })}
        </div>
      )}
      <div className={styles.stats}>
        <Stat label={`盈利${mode === "month" ? "天数" : "月数"}`} value={`${stats.profitDays} ${unit}`} sub={`亏损 ${stats.lossDays} ${unit}`} />
        <Stat label={`最好的${noun}`} value={stats.best ? formatPnl(stats.best.pnl) : "--"} sub={when(stats.best, mode)} cls={tone(stats.best?.pnl)} />
        <Stat label={`最差的${noun}`} value={stats.worst ? formatPnl(stats.worst.pnl) : "--"} sub={when(stats.worst, mode)} cls={tone(stats.worst?.pnl)} />
        <Stat label={mode === "month" ? "日均" : "月均"} value={formatPnl(stats.average)} cls={tone(stats.average)} />
      </div>
    </section>
  );
}
